import React from 'react';
import { Users, CheckCircle, UserCheck, TrendingUp, CalendarPlus, Eye, AlertTriangle, ShieldAlert, Ban, Mail } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color = 'blue', subtitle }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const PlanBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-16">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-gray-900 w-10 text-right">{count}</span>
    </div>
  );
};

const AdminOverview = ({ stats }) => {
  if (!stats) return null;

  const totalPlan = (stats.plan_distribution?.basic || 0) + (stats.plan_distribution?.plus || 0) + (stats.plan_distribution?.pro || 0);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  return (
    <div className="space-y-8">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Users}
          label="Dienstleister gesamt"
          value={stats.total_providers || 0}
          color="blue"
        />
        <StatCard
          icon={CheckCircle}
          label="Aktive Abos"
          value={stats.active_subscriptions || 0}
          color="green"
          subtitle={`${stats.trial_subscriptions || 0} in Testphase`}
        />
        <StatCard
          icon={UserCheck}
          label="Kunden gesamt"
          value={stats.total_customers || 0}
          color="indigo"
        />
        <StatCard
          icon={TrendingUp}
          label="Umsatz (Stripe)"
          value={formatCurrency(stats.total_revenue_cents || 0)}
          color="emerald"
        />
        <StatCard
          icon={CalendarPlus}
          label="Neue Anmeldungen (30 Tage)"
          value={stats.new_signups_30d || 0}
          color="purple"
        />
        <StatCard
          icon={Eye}
          label="Profilaufrufe (30 Tage)"
          value={stats.page_views_30d || 0}
          color="orange"
          subtitle={`${stats.total_page_views || 0} gesamt`}
        />
        {stats.waiting_list && (
          <StatCard
            icon={Mail}
            label="Warteliste"
            value={stats.waiting_list.active || 0}
            color="cyan"
            subtitle={`${stats.waiting_list.total || 0} gesamt`}
          />
        )}
      </div>

      {/* Moderation Alerts */}
      {((stats.moderation?.pending || 0) > 0 || (stats.moderation?.flagged || 0) > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Moderationshinweise
          </h3>
          <div className="flex gap-6">
            {(stats.moderation?.pending || 0) > 0 && (
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  <strong>{stats.moderation.pending}</strong> ausstehende Überprüfung
                </span>
              </div>
            )}
            {(stats.moderation?.flagged || 0) > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-800">
                  <strong>{stats.moderation.flagged}</strong> markierte Profile
                </span>
              </div>
            )}
            {(stats.moderation?.blocked || 0) > 0 && (
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-800">
                  <strong>{stats.moderation.blocked}</strong> blockierte Profile
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plan Distribution */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan-Verteilung</h3>
        <div className="space-y-3">
          <PlanBar label="Basic" count={stats.plan_distribution?.basic || 0} total={totalPlan} color="bg-[#2B2142]" />
          <PlanBar label="Plus" count={stats.plan_distribution?.plus || 0} total={totalPlan} color="bg-indigo-500" />
          <PlanBar label="Pro" count={stats.plan_distribution?.pro || 0} total={totalPlan} color="bg-purple-500" />
        </div>
      </div>

      {/* Subscription Status Distribution */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Abo-Status Übersicht</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <p className="text-2xl font-bold text-green-700">{stats.subscription_distribution?.active || 0}</p>
            <p className="text-sm text-green-600">Aktiv</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-xl">
            <p className="text-2xl font-bold text-yellow-700">{stats.subscription_distribution?.trial || 0}</p>
            <p className="text-sm text-yellow-600">Testphase</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-xl">
            <p className="text-2xl font-bold text-red-700">{stats.subscription_distribution?.expired || 0}</p>
            <p className="text-sm text-red-600">Abgelaufen</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Weitere Kennzahlen</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{stats.total_users || 0}</p>
            <p className="text-xs text-gray-500">Nutzer gesamt</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{stats.total_requests || 0}</p>
            <p className="text-xs text-gray-500">Anfragen gesamt</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{stats.total_page_views || 0}</p>
            <p className="text-xs text-gray-500">Profilaufrufe gesamt</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{stats.expired_subscriptions || 0}</p>
            <p className="text-xs text-gray-500">Abgelaufene Abos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
